"use client";

import { memo, ReactNode, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useReportData } from "../../../../hooks/useReportData";
import StateCard from "../../../shared/StateCard";
import type { AiReportData } from "@/types/endpoints/reports/GetAiReportById";
import {
  CHART_BLOCK_CARD_CLASSES,
  CHART_BLOCK_HEADER_CLASSES,
  CHART_BLOCK_TITLE_CLASSES,
  CHART_BLOCK_CONTENT_CLASSES
} from "../../../shared/cardStyles";
import { ArrowLeft, ArrowLeftRight } from "lucide-react";
import { LeftopenArrowIcon } from "@/components/icons";

export type ReportType = 'actions' | 'answers' | 'q_density' | 'transcribe' | 'evaluation';

interface BaseChartBlockProps {
  attemptId: number;
  reportType: ReportType;
  titleKey: string;
  headerExtra?: ReactNode | ((report: AiReportData) => ReactNode);
  headerLayout?: 'row' | 'col';
  children: (report: AiReportData) => ReactNode;
  onEmpty?: () => ReactNode;
  className?: string;
  contentClassName?: string;
  showModeToggle?: boolean;
  displayMode?: "minutes" | "questions";
  onDisplayModeChange?: (mode: "minutes" | "questions") => void;
  onAnalyticsData?: (data: any) => void;
  showFullAnalysisButton?: boolean;
  onOpenFullAnalysis?: () => void;
}

export const BaseChartBlock = memo(function BaseChartBlock({
  attemptId,
  reportType,
  titleKey,
  headerExtra,
  headerLayout = 'row',
  children,
  onEmpty,
  className,
  contentClassName,
  showModeToggle,
  displayMode = "minutes",
  onDisplayModeChange,
  onAnalyticsData,
  showFullAnalysisButton = false,
  onOpenFullAnalysis,
}: BaseChartBlockProps) {
  const t = useTranslations();
  const { data: report, isLoading } = useReportData<typeof reportType>(attemptId, reportType);

  // Передаем данные аналитики наверх, когда получаем report
  useEffect(() => {
    if (report && onAnalyticsData) {
      const reportData = Array.isArray(report) ? report[0] : report;
      onAnalyticsData(reportData);
    }
  }, [report, onAnalyticsData]);

  // Toggle handler
  const handleToggle = () => {
    if (onDisplayModeChange) {
      const newMode = displayMode === "minutes" ? "questions" : "minutes";
      onDisplayModeChange(newMode);
    }
  };

  // Loading state
  if (isLoading) {
    return <StateCard type="loading" />;
  }

  // Empty state
  if (!report) {
    if (onEmpty) {
      return <>{onEmpty()}</>;
    }
    return <StateCard type="empty" />;
  }

  // Success state
  // Если report - это массив, берем первый элемент
  const reportData = Array.isArray(report) ? report[0] : report;

  const headerClasses = headerLayout === 'col'
    ? 'flex flex-col items-start gap-3'
    : 'flex items-center justify-between';

  return (
    <Card className={className || CHART_BLOCK_CARD_CLASSES}>
      <CardHeader className={CHART_BLOCK_HEADER_CLASSES}>
        <div className={headerClasses}>
          <CardTitle className={CHART_BLOCK_TITLE_CLASSES}>
            {t(titleKey)}
          </CardTitle>
          <div className="flex items-center gap-2">
            {showModeToggle && (
              <Button
                variant="ghost"
                onClick={handleToggle}
                size="sm"
                className="rounded-sm bg-[#F5F5F5] text-sm font-normal p-3!"
              >
                {displayMode === "minutes"
                  ? t("label-by-questions")
                  : t("label-by-minutes")
                }
                <ArrowLeftRight />
              </Button>
            )}
            {typeof headerExtra === 'function' ? headerExtra(reportData as AiReportData) : headerExtra}
          </div>
        </div>
      </CardHeader>
      <CardContent className={contentClassName || CHART_BLOCK_CONTENT_CLASSES}>
        {children(reportData as AiReportData)}
      </CardContent>
      {showFullAnalysisButton && (
        <div className="flex justify-start">
          <Button
            variant="outline"
            className="p-2 rounded-sm"
            onClick={onOpenFullAnalysis}
          >
            <LeftopenArrowIcon />
            {t("btn-open-all-charts")}
          </Button>
        </div>
      )}
    </Card>
  );
});

export default BaseChartBlock;
